'use client';

import { useAuth } from '@/contexts/AuthContext';
import { deleteChangelogEntry } from '@/lib/changelog';
import Link from 'next/link';
import { useState } from 'react';
import { Trash2, Edit } from 'lucide-react';

interface ChangelogEntryAdminControlsProps {
  entryId: string;
  onDeleted?: () => void;
}

export function ChangelogEntryAdminControls({
  entryId,
  onDeleted,
}: ChangelogEntryAdminControlsProps) {
  const { isSuperAdmin, loading } = useAuth();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  if (loading || !isSuperAdmin) {
    return null;
  }

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteChangelogEntry(entryId);
      onDeleted?.();
    } catch (error) {
      console.error('Error deleting entry:', error);
      alert('Failed to delete entry');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Link
        href="/admin/changelog"
        className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded transition-colors"
      >
        <Edit className="w-4 h-4" />
        Edit
      </Link>

      {showDeleteConfirm ? (
        <div className="flex items-center gap-2 ml-2">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Delete this post?
          </p>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="px-3 py-1 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium rounded transition-colors"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
          <button
            onClick={() => setShowDeleteConfirm(false)}
            disabled={isDeleting}
            className="px-3 py-1 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-700 disabled:opacity-50 text-gray-900 dark:text-white text-sm font-medium rounded transition-colors"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="inline-flex items-center gap-2 px-3 py-2 bg-red-100 hover:bg-red-200 dark:bg-red-900 dark:hover:bg-red-800 text-red-700 dark:text-red-300 text-sm font-medium rounded transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          Delete
        </button>
      )}
    </div>
  );
}
